import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IFeatureGroup, IFeatureGroupState } from "./types";
import {
  getAllFeatureGroups,
  getFeatureGroupById,
  createFeatureGroup,
  updateFeatureGroup,
  deleteFeatureGroup,
  getAvailableFeatureGroups,
} from "./thunks";

const initialState: IFeatureGroupState = {
  featureGroupList: [],
  currentFeatureGroup: {} as IFeatureGroup,
  isModalOpen: false,
  isLoading: false,
};

const featureGroupSlice = createSlice({
  name: "featureGroup",
  initialState,
  reducers: {
    setFeatureGroupList: (state, action: PayloadAction<IFeatureGroup[]>) => {
      state.featureGroupList = action.payload;
    },
    setCurrentFeatureGroup: (state, action: PayloadAction<IFeatureGroup>) => {
      state.currentFeatureGroup = action.payload;
    },
    setIsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    addFeatureGroup: (state, action: PayloadAction<IFeatureGroup>) => {
      state.featureGroupList.push(action.payload);
    },
    updateFeatureGroupInList: (state, action: PayloadAction<IFeatureGroup>) => {
      const index = state.featureGroupList.findIndex(
        (group: IFeatureGroup) => group.id === action.payload.id
      );
      if (index !== -1) {
        state.featureGroupList[index] = action.payload;
      }
    },
    removeFeatureGroup: (state, action: PayloadAction<string>) => {
      state.featureGroupList = state.featureGroupList.filter(
        (group: IFeatureGroup) => group.id !== action.payload
      );
    },
    clearCurrentFeatureGroup: (state) => {
      state.currentFeatureGroup = {} as IFeatureGroup;
    },
  },
  extraReducers: (builder) => {
    // Get all feature groups
    builder
      .addCase(getAllFeatureGroups.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllFeatureGroups.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featureGroupList = action.payload;
      })
      .addCase(getAllFeatureGroups.rejected, (state) => {
        state.isLoading = false;
      });

    // Get feature group by ID
    builder
      .addCase(getFeatureGroupById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFeatureGroupById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentFeatureGroup = action.payload;
      })
      .addCase(getFeatureGroupById.rejected, (state) => {
        state.isLoading = false;
      });

    // Create feature group
    builder
      .addCase(createFeatureGroup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createFeatureGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featureGroupList.push(action.payload);
      })
      .addCase(createFeatureGroup.rejected, (state) => {
        state.isLoading = false;
      });

    // Update feature group
    builder
      .addCase(updateFeatureGroup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateFeatureGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.featureGroupList.findIndex(
          (group: IFeatureGroup) => group.id === action.payload.id
        );
        if (index !== -1) {
          state.featureGroupList[index] = action.payload;
        }
      })
      .addCase(updateFeatureGroup.rejected, (state) => {
        state.isLoading = false;
      });

    // Delete feature group
    builder
      .addCase(deleteFeatureGroup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteFeatureGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featureGroupList = state.featureGroupList.filter(
          (group: IFeatureGroup) => group.id !== action.payload
        );
      })
      .addCase(deleteFeatureGroup.rejected, (state) => {
        state.isLoading = false;
      });

    // Get available feature groups (from reflection)
    builder
      .addCase(getAvailableFeatureGroups.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAvailableFeatureGroups.fulfilled, (state, action) => {
        state.isLoading = false;
        // This could be used to populate feature groups from reflection
        // For now, we'll just set loading to false
      })
      .addCase(getAvailableFeatureGroups.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  setFeatureGroupList,
  setCurrentFeatureGroup,
  setIsModalOpen,
  setIsLoading,
  addFeatureGroup,
  updateFeatureGroupInList,
  removeFeatureGroup,
  clearCurrentFeatureGroup,
} = featureGroupSlice.actions;

export default featureGroupSlice.reducer;
